import React, { useState } from 'react';
import { ImportService } from './ImportService';
import { 
    Row, 
    Col, 
    Button, 
    CardBody, 
    CardHeader, 
    Alert, 
    Spinner
} from 'reactstrap';

const Import = () => {
    const importService = React.useMemo(() => new ImportService(), []);
    const [loading, setLoading] = useState(false);
    const [importData, setImportData] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [error, setError] = useState(null);

    const analyzeLocalStorageData = () => {
        try {
            const data = importService.extractLocalStorageData();
            setImportData(data);
            setError(null);
        } catch (err) {
            setError('Σφάλμα κατά την ανάλυση των τοπικών δεδομένων: ' + err.message);
        }
    };

    const importToBackend = async () => {
        if (!importData) {
            setError('Παρακαλούμε εκτελέστε πρώτα ανάλυση δεδομένων');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            const result = await importService.importData(importData);
            setImportResult(result);

            if (result.success) {
                importService.clearLocalStorageData();
                setImportData(null);
            }
        } catch (err) {
            setError('Η εισαγωγή απέτυχε: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const resetImport = () => {
        setImportData(null);
        setImportResult(null);
        setError(null);
    };

    return (
        <div>
            <div className="ek-card mb-4">
                <CardHeader className="ek-card-header">
                    <h5>
                        <i className="bi bi-database-fill-up text-primary"></i> Εργαλείο Εισαγωγής & Μετανάστευσης Δεδομένων
                    </h5>
                </CardHeader>
                <CardBody className="ek-card-body">
                    <p className="text-muted">
                        Αυτό το εργαλείο επιτρέπει τη μεταφορά όλων των υπαρχόντων δεδομένων (Προϊόντα, Συστατικά, Συνταγές, Εντολές) από το τοπικό `localStorage` στη βάση δεδομένων του backend.
                    </p>

                    {error && (
                        <Alert color="danger" className="d-flex align-items-center gap-2">
                            <i className="bi bi-exclamation-triangle-fill fs-5"></i>
                            <div>{error}</div>
                        </Alert>
                    )}

                    {importResult && (
                        <Alert color={importResult.success ? "success" : "danger"} className="d-flex align-items-start gap-2">
                            <i className={`bi ${importResult.success ? 'bi-check-circle-fill' : 'bi-exclamation-octagon-fill'} fs-4 mt-1`}></i>
                            <div>
                                <h6 className="fw-bold mb-1">{importResult.success ? "Επιτυχής Εισαγωγή!" : "Σφάλμα Εισαγωγής"}</h6>
                                <p className="mb-1">{importResult.message}</p>
                                {importResult.details && (
                                    <small className="d-block text-muted">
                                        Εισήχθησαν: {importResult.details.importedIngredients} Συστατικά, {importResult.details.importedProducts} Προϊόντα, {importResult.details.importedRecipies} Συνταγές, {importResult.details.importedCommands} Εντολές.
                                    </small>
                                )}
                            </div>
                        </Alert>
                    )}

                    <div className="d-flex gap-2">
                        <Button 
                            color="primary" 
                            onClick={analyzeLocalStorageData}
                            disabled={loading}
                        >
                            <i className="bi bi-search me-1"></i> Βήμα 1: Ανάλυση Δεδομένων localStorage
                        </Button>

                        {(importData || importResult) && (
                            <Button color="secondary" onClick={resetImport}>
                                <i className="bi bi-arrow-counterclockwise me-1"></i> Καθαρισμός
                            </Button>
                        )}
                    </div>
                </CardBody>
            </div>

            {importData && (
                <div className="ek-card mb-4">
                    <CardHeader className="ek-card-header">
                        <h5>
                            <i className="bi bi-clipboard-data text-primary"></i> Βήμα 2: Προεπισκόπηση Δεδομένων προς Εισαγωγή
                        </h5>
                    </CardHeader>
                    <CardBody className="ek-card-body">
                        <Row className="g-3 mb-4">
                            <Col md="3">
                                <div className="p-3 bg-light rounded-3 border text-center">
                                    <i className="bi bi-pie-chart-fill text-primary fs-3"></i>
                                    <h4 className="mt-2 mb-0 fw-bold">{importData.ingredients?.length || 0}</h4>
                                    <small className="text-muted">Συστατικά</small>
                                </div>
                            </Col>
                            <Col md="3">
                                <div className="p-3 bg-light rounded-3 border text-center">
                                    <i className="bi bi-box-seam text-info fs-3"></i>
                                    <h4 className="mt-2 mb-0 fw-bold">{importData.products?.length || 0}</h4>
                                    <small className="text-muted">Προϊόντα</small>
                                </div>
                            </Col>
                            <Col md="3">
                                <div className="p-3 bg-light rounded-3 border text-center">
                                    <i className="bi bi-journal-bookmark-fill text-warning fs-3"></i>
                                    <h4 className="mt-2 mb-0 fw-bold">{importData.recipies?.length || 0}</h4>
                                    <small className="text-muted">Συνταγές</small>
                                </div>
                            </Col>
                            <Col md="3">
                                <div className="p-3 bg-light rounded-3 border text-center">
                                    <i className="bi bi-clipboard2-check-fill text-success fs-3"></i>
                                    <h4 className="mt-2 mb-0 fw-bold">{importData.commands?.length || 0}</h4>
                                    <small className="text-muted">Εντολές</small>
                                </div>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end gap-2 pt-3 border-top">
                            <Button 
                                color="success" 
                                size="lg" 
                                onClick={importToBackend}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Εισαγωγή σε εξέλιξη...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-cloud-arrow-up-fill me-1"></i> Βήμα 3: Εκτέλεση Εισαγωγής στη Βάση
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardBody>
                </div>
            )}
        </div>
    );
};

export { Import };
export default Import;
